import FileUpload from "@bevco/MobiusOverrides/FileUpload/FileUpload";
import setDynamicFormFieldValue from "@bevco/Store/Handlers/DynamicForm/SetFieldValue";
import { CustomState } from "@bevco/Store/Reducers";
import { DynamicFormContext } from "@bevco/Widgets/DynamicForm/DynamicForm";
import { useDynamicFormFileUpload } from "@bevco/Widgets/DynamicForm/useDynamicFormFileUpload";
import { useRegisterDynamicFormAttachments } from "@bevco/Widgets/DynamicForm/useRegisterDynamicFormAttachments";
import { useRegisterDynamicFormFieldKeys } from "@bevco/Widgets/DynamicForm/useRegisterDynamicFormFieldKeys";
import setIsBadFile from "@insite/client-framework/Store/Components/OrderUpload/Handlers/SetIsBadFile";
import setUploadLimitExceeded from "@insite/client-framework/Store/Components/OrderUpload/Handlers/SetUploadLimitExceeded";
import translate from "@insite/client-framework/Translate";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import React from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const fileUploadLabel: TypographyPresentationProps = {
    weight: 700,
    css: css`
        margin-bottom: 10px;
    `,
};

type OwnProps = {
    widgetId: string;
    fieldKey: string;
    label: string;
    isRequired: boolean;
    storedFileValue?: string;
    formCleared: boolean;
    skipRegistration?: boolean;
};

const mapDispatchToProps = {
    setDynamicFormFieldValue,
    setIsBadFile,
    setUploadLimitExceeded,
};

type Props = OwnProps & ResolveThunks<typeof mapDispatchToProps>;

/** Shared PDF file input used by Dynamic Form upload widgets. */
const DynamicFileUploadField: React.FC<Props> = ({
    widgetId,
    fieldKey,
    label,
    isRequired,
    storedFileValue,
    formCleared,
    skipRegistration = false,
    setDynamicFormFieldValue,
    setIsBadFile,
    setUploadLimitExceeded,
}) => {
    const nameKey = `${fieldKey}Name`;
    const formContext = React.useContext(DynamicFormContext);

    useRegisterDynamicFormFieldKeys(widgetId, [fieldKey], !skipRegistration);
    useRegisterDynamicFormAttachments(widgetId, [{ fileKey: fieldKey, nameKey }], !skipRegistration);

    const { fileField, errorMessage, fileUploadRef, handleFileChange, clearLocalFile, validate } =
        useDynamicFormFileUpload({
            fieldKey,
            nameKey,
            setDynamicFormFieldValue,
            setIsBadFile,
            setUploadLimitExceeded,
        });

    React.useEffect(() => {
        formContext.validators[fieldKey] = () => {
            if (!isRequired && !fileField) {
                return true;
            }
            return validate();
        };
        return () => {
            formContext.validators[fieldKey] = undefined;
        };
    }, [fieldKey, isRequired, fileField, validate, formContext.validators]);

    React.useEffect(() => {
        if (formCleared && !storedFileValue) {
            clearLocalFile();
        }
    }, [formCleared, storedFileValue, clearLocalFile]);

    return (
        <>
            <Typography {...fileUploadLabel}>{label}</Typography>
            <FileUpload
                inputRef={fileUploadRef}
                fileName={fileField?.name || ""}
                accept=".pdf"
                error={errorMessage}
                onFileChange={handleFileChange}
                buttonText={translate("Add a file")}
                placeholder={translate("No file selected")}
            />
        </>
    );
};

const ConnectedDynamicFileUploadField = connect(
    null,
    mapDispatchToProps,
)(DynamicFileUploadField);

export default ConnectedDynamicFileUploadField;
